import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import EventService from "service/event/EventService";
import AppointmentService from "../service/AppointmentService";
import Dialog from "./Dialog";
import { STATUS_LABEL } from "../helper/appointmentConstants";

/**
 * The confirmation for the four cancel-family statuses — a mandatory REASON and
 * one Confirm.
 *
 * NO REFUND CHOICE, by decision (2026-09-14): refunds are the expert dashboard's,
 * so this dialog offers no "with / without refund" faces, shows no refund amount
 * and has no force-refund override, and the change it confirms carries no
 * refundRequest. What happens to the money is decided server-side — see
 * useStatusChange for exactly what that means.
 */
/** Bridge's synthetic chip; picking it reveals the free-text box. */
const OTHER_REASON = "Other reason";

const CancelAppointmentDialog = ({ appointment, statusToApply, onConfirm, onClose }) => {
  /* The reason is MANDATORY, and not only as a UI nicety: the server writes it
     to the appointment as `updateReason` and into the audit trail, and it does
     so UNCONDITIONALLY — a cancel that sends none BLANKS whatever was stored
     (repository/appointment.go:2057). phr captured nothing at all before this. */
  const [reasons, setReasons] = useState([]);
  const [picked, setPicked] = useState(null);
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    new AppointmentService().getCancellationReasons(
      (res) => setReasons(Array.isArray(res) ? res : []),
      // Silent: the operator can still cancel via "Other reason", which is the
      // one chip that does not come from the server.
      () => setReasons([])
    );
  }, []);

  const reasonText = () => (picked === OTHER_REASON ? otherText.trim() : picked || "");

  /** Bridge's hasReason, including its two exact messages. */
  const requireReason = () => {
    if (picked == null) {
      EventService.emit("showError", { message: "Please select a reason for cancellation" });
      return false;
    }
    if (picked === OTHER_REASON && otherText.trim() === "") {
      EventService.emit("showError", { message: "Please add a comment for the other reason" });
      return false;
    }
    return true;
  };

  /** The only way out that sends anything — so nothing can bypass the reason. */
  const confirm = () => {
    if (!requireReason()) return;
    onConfirm(reasonText());
  };

  if (!appointment) return null;

  const label = (STATUS_LABEL[statusToApply] || "cancelled").toLowerCase();

  return (
    <Dialog title="Are you sure you want to cancel the appointment?" onClose={onClose}>
      <p className="zy-dialog-lede">
        Appointment <strong>{appointment.id}</strong>
        {appointment.patientName ? ` for ${appointment.patientName}` : ""} will be
        marked as <strong>{label}</strong>.
      </p>

      {/* Mandatory, and gated in `requireReason` rather than by disabling the
          buttons — bridge toasts the reason instead of leaving a dead control,
          and a disabled confirm with no explanation is the worse of the two. */}
      <div className="zy-reason">
        <p className="zy-reason-cap">
          Please add a reason below<span className="zy-reason-req"> *</span>
        </p>
        <div className="zy-reason-chips">
          {reasons.concat([OTHER_REASON]).map((r) => (
            <button
              key={r}
              type="button"
              className={`zy-reason-chip${picked === r ? " is-on" : ""}`}
              aria-pressed={picked === r}
              onClick={() => {
                setPicked(r);
                if (r !== OTHER_REASON) setOtherText("");
              }}
            >
              {r}
            </button>
          ))}
        </div>
        {picked === OTHER_REASON && (
          <textarea
            className="zy-reason-text"
            rows={2}
            autoFocus
            placeholder="Add a comment"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
          />
        )}
      </div>

      <div className="zy-dialog-actions">
        <button type="button" className="zy-btn zy-btn-primary" onClick={confirm}>
          Confirm
        </button>
        <button type="button" className="zy-btn zy-btn-ghost" onClick={onClose}>
          Go back
        </button>
      </div>
    </Dialog>
  );
};

CancelAppointmentDialog.propTypes = {
  appointment: PropTypes.object,
  statusToApply: PropTypes.number,
  /** Called with the reason text once one has been given. */
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CancelAppointmentDialog;
